import React, { useState, useEffect } from 'react';
import { FiCheck, FiSend, FiEye, FiDownload, FiCalendar, FiUser, FiDollarSign } from 'react-icons/fi';
import { PayrollService } from '../../services/payrollService';
import ConfirmationModal from '../common/ConfirmationModal';
import { useConfirmation } from '../../hooks/useConfirmation';

export default function PayrollReview() {
  const { confirmationState, showConfirmation, handleConfirm, handleCancel } = useConfirmation();
  const [pendingPayslips, setPendingPayslips] = useState([]);
  const [selectedPayslips, setSelectedPayslips] = useState([]);
  const [showDetails, setShowDetails] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadPendingPayslips();
    
    // Listen for payroll generation events
    const handlePayrollGenerated = (event) => {
      loadPendingPayslips();
    };
    
    window.addEventListener('payroll-generated', handlePayrollGenerated);
    return () => window.removeEventListener('payroll-generated', handlePayrollGenerated);
  }, []);

  const loadPendingPayslips = async () => {
    try {
      const pending = await PayrollService.getPendingPayslips();
      setPendingPayslips(pending || []);
      setSelectedPayslips((pending || []).map(p => p.id)); // Select all by default
    } catch (error) {
      console.error('Error loading pending payslips:', error);
      setPendingPayslips([]);
      setSelectedPayslips([]);
    }
  };

  const handleGenerateNow = async () => {
    setIsLoading(true);
    try {
      const payslips = await PayrollService.generatePayslips();
      await PayrollService.savePayslips(payslips);
      await loadPendingPayslips();
    } catch (error) {
      console.error('Error generating payslips:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    if (selectedPayslips.length === 0) return;
    
    const confirmed = await showConfirmation({
      title: "Approve Payslips",
      message: `Approve ${selectedPayslips.length} payslip(s)? This will mark them as approved and ready for payment.`,
      confirmText: "Approve",
      cancelText: "Cancel",
      type: "success"
    });
    
    if (confirmed) {
      try {
        await PayrollService.approvePayslips(selectedPayslips);
        await loadPendingPayslips();
      } catch (error) {
        console.error('Error approving payslips:', error);
      }
    }
  };

  const handleRelease = async () => {
    if (selectedPayslips.length === 0) return;
    
    const confirmed = await showConfirmation({
      title: 'Release Payslips',
      message: `Are you sure you want to release ${selectedPayslips.length} payslips? This will email them to the respective staff members.`,
      confirmText: 'Release',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (confirmed) {
      try {
        await PayrollService.releasePayslips(selectedPayslips);
        await loadPendingPayslips();
      } catch (error) {
        console.error('Error releasing payslips:', error);
      }
    }
  };

  const togglePayslipSelection = (payslipId) => {
    setSelectedPayslips(prev => 
      prev.includes(payslipId)
        ? prev.filter(id => id !== payslipId)
        : [...prev, payslipId]
    );
  };

  const toggleDetails = (payslipId) => {
    setShowDetails(prev => ({
      ...prev,
      [payslipId]: !prev[payslipId]
    }));
  };

  const formatCurrency = (amount) => {
    return `£${amount.toFixed(2)}`;
  };

  const getMonthName = (month) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[month - 1];
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-gray-900">Payroll Review</h3>
        <div className="flex space-x-3">
          <button
            onClick={handleGenerateNow}
            disabled={isLoading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <FiCalendar size={16} />
            <span>{isLoading ? 'Generating...' : 'Run Payroll Now'}</span>
          </button>
        </div>
      </div>

      {pendingPayslips.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <FiDollarSign className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Payslips</h3>
          <p className="text-gray-500 mb-4">
            There are no payslips pending review. Payroll is automatically generated on the last day of each month.
          </p>
          <button
            onClick={handleGenerateNow}
            disabled={isLoading}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiCalendar size={16} />
            <span>Generate Payroll Now</span>
          </button>
        </div>
      ) : (
        <>
          {/* Action Bar */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {selectedPayslips.length} of {pendingPayslips.length} payslips selected
                </span>
                <button
                  onClick={() => setSelectedPayslips(
                    selectedPayslips.length === pendingPayslips.length 
                      ? [] 
                      : pendingPayslips.map(p => p.id)
                  )}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {selectedPayslips.length === pendingPayslips.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleApprove}
                  disabled={selectedPayslips.length === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiCheck size={16} />
                  <span>Mark Correct</span>
                </button>
                
                <button
                  onClick={handleRelease}
                  disabled={selectedPayslips.length === 0}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiSend size={16} />
                  <span>Release Payslips</span>
                </button>
              </div>
            </div>
          </div>

          {/* Payslips List */}
          <div className="space-y-4">
            {pendingPayslips.map((payslip) => (
              <div key={payslip.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <input
                        type="checkbox"
                        checked={selectedPayslips.includes(payslip.id)}
                        onChange={() => togglePayslipSelection(payslip.id)}
                        className="rounded"
                      />
                      
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-full">
                          <FiUser className="text-blue-600" size={16} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{payslip.staffName}</h4>
                          <p className="text-sm text-gray-500 capitalize">{payslip.role}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6">
                      <div className="text-right">
                        <p className="text-sm text-gray-500">
                          {getMonthName(payslip.month)} {payslip.year}
                        </p>
                        <p className="text-sm text-gray-500">
                          {payslip.appointmentCount} appointments
                        </p>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-lg font-bold text-green-600">
                          {formatCurrency(payslip.totalEarnings)}
                        </p>
                      </div>
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => toggleDetails(payslip.id)}
                          className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <FiEye size={14} />
                          <span>{showDetails[payslip.id] ? 'Hide' : 'View'} Details</span>
                        </button>
                        
                        <button className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:bg-gray-50 rounded transition-colors">
                          <FiDownload size={14} />
                          <span>Export</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Appointment Details */}
                {showDetails[payslip.id] && (
                  <div className="border-t border-gray-200 bg-gray-50 p-4">
                    <h5 className="font-medium text-gray-900 mb-3">Appointment Details</h5>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Staff %</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Earning</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {payslip.appointmentDetails.map((apt, index) => (
                            <tr key={index} className="text-sm">
                              <td className="px-3 py-2 text-gray-900">
                                {new Date(apt.date).toLocaleDateString()}
                              </td>
                              <td className="px-3 py-2 text-gray-500">{apt.time}</td>
                              <td className="px-3 py-2 text-gray-900">{apt.client}</td>
                              <td className="px-3 py-2 text-gray-500">{apt.type}</td>
                              <td className="px-3 py-2 text-gray-500">{apt.duration}min</td>
                              <td className="px-3 py-2 text-gray-500">{formatCurrency(apt.rate)}</td>
                              <td className="px-3 py-2 text-gray-500">{apt.rolePercentage}%</td>
                              <td className="px-3 py-2 text-right font-medium text-green-600">
                                {formatCurrency(apt.earning)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationState.isOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title={confirmationState.title}
        message={confirmationState.message}
        confirmText={confirmationState.confirmText}
        cancelText={confirmationState.cancelText}
        type={confirmationState.type}
      />
    </div>
  );
}
